import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { 
  ChevronDownIcon, 
  CheckIcon, 
  PlusIcon, 
  UserIcon,
  EnvelopeIcon,
  PhoneIcon 
} from '@heroicons/react/24/outline';
import { Button, Modal, PhoneInput, useToast } from '../ui';
import { Customer } from '../config/supabase';
import { useCustomersAll, useCreateCustomer } from '../hooks/api/useCustomers';



interface CustomerSelectProps {
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  onCustomerAdded?: (customer: Customer) => void;
}

const CustomerSelect: React.FC<CustomerSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select customer or add new',
  disabled = false,
  className,
  size = 'md',
  onCustomerAdded
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { success, error: showError } = useToast();

  // Form state for add customer modal
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');

  // React Query hooks
  const { data: customers = [] } = useCustomersAll();
  const createCustomerMutation = useCreateCustomer();

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };



  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);



  const handleAddCustomer = async (values: Record<string, any>) => {
    try {
      const newCustomer = await createCustomerMutation.mutateAsync(values as any);
      
      setSelectedValue(newCustomer.id);
      handleCloseAddModal();
      
      success('Customer added successfully');
      
      // Notify parent components
      onChange?.(newCustomer.id);
      onCustomerAdded?.(newCustomer);
    } catch (error: any) {
      showError('Error adding customer: ' + error.message);
    }
  };

  const selectedCustomer = customers.find(customer => customer.id === selectedValue);
  
  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (customer: Customer) => {
    setSelectedValue(customer.id);
    setIsOpen(false);
    setSearchTerm('');
    onChange?.(customer.id);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleQuickAdd = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsAddModalOpen(true);
    setIsOpen(false);
    // Initialize form with search term if available
    setNewCustomerName(searchTerm || '');
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    // Reset form state
    setNewCustomerName('');
    setNewCustomerEmail('');
    setNewCustomerPhone('');
    setNewCustomerAddress('');
  };

  return (
    <>
      <div ref={selectRef} className={clsx('relative', className)}>
        <div
          className={clsx(
            'flex items-center justify-between w-full border rounded-lg bg-white cursor-pointer transition-colors',
            sizeClasses[size],
            disabled 
              ? 'bg-gray-50 cursor-not-allowed border-gray-200 text-gray-400'
              : isOpen
              ? 'border-blue-500 ring-1 ring-blue-500'
              : 'border-gray-300 hover:border-blue-400'
          )}
          onClick={handleToggle}
        >
          <div className="flex items-center flex-1">
            {selectedCustomer ? (
              <div className="flex items-center">
                <div className="flex-shrink-0 h-6 w-6 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                  <UserIcon className="h-3 w-3 text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{selectedCustomer.name}</div>
                  {selectedCustomer.email && (
                    <div className="text-xs text-gray-500">{selectedCustomer.email}</div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          
          <ChevronDownIcon 
            className={clsx(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
            <div className="p-3 border-b border-gray-200">
              <input
                ref={searchRef}
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="max-h-48 overflow-y-auto">
              {/* Quick Add Button */}
              <div 
                className="flex items-center px-3 py-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleQuickAdd(e);
                }}
              >
                <div className="flex-shrink-0 h-8 w-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <PlusIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="font-medium text-green-600">Add New Customer</div>
                  <div className="text-xs text-gray-500">Quickly add a new customer</div>
                </div>
              </div>

              {filteredCustomers.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No customers found
                  {searchTerm && (
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleQuickAdd(e);
                        }}
                        className="text-xs"
                      >
                        Add "{searchTerm}" as new customer
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={clsx(
                      'flex items-center px-3 py-3 cursor-pointer',
                      selectedValue === customer.id
                        ? 'bg-blue-50 text-blue-900'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => handleSelect(customer)}
                  >
                    <div className="flex-shrink-0 h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                      <UserIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900 truncate">{customer.name}</div>
                        {selectedValue === customer.id && (
                          <CheckIcon className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        {customer.email && (
                          <div className="flex items-center mr-3">
                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                            {customer.email}
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center">
                            <PhoneIcon className="h-3 w-3 mr-1" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Add Customer Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Add New Customer"
        size="md"
        maskClosable={false}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                name="name"
                type="text"
                required
                autoFocus
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter customer's full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                name="email"
                type="email"
                value={newCustomerEmail}
                onChange={(e) => setNewCustomerEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="customer@example.com"
              />
            </div>

            <div>
              <PhoneInput
                label="Phone Number"
                name="phone"
                placeholder="555-123-4567"
                value={newCustomerPhone}
                onChange={(value) => setNewCustomerPhone(value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={newCustomerAddress}
                onChange={(e) => setNewCustomerAddress(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Enter customer's address..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCloseAddModal();
              }}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="primary" 
              loading={createCustomerMutation.isPending}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                const values = {
                  name: newCustomerName.trim(),
                  email: newCustomerEmail.trim(),
                  phone: newCustomerPhone.trim(),
                  address: newCustomerAddress.trim(),
                };
                
                if (!values.name) {
                  showError('Customer name is required');
                  return;
                }
                
                await handleAddCustomer(values);
              }}
            >
              Add Customer
            </Button>
          </div>
        </div>
        </div>
      </Modal>
    </>
  );
};

export default CustomerSelect; 